import { v4 as uuidv4 } from 'uuid';

import {
    addFieldsToArray,
    buildField,
    isDirtyTab,
    removeFromArray,
    updateTabField,
    updateTabFieldArray
} from '../../common/utils';
import { CUSTOM_INSIGHTS_FIELDS, IS_NEW_FIELD, NAMING_CONVENTIONS_FIELDS } from '../../common/fields';
import * as actions from './customInsightsActions';
import { commonActionsReducer } from '../../common/actions';
import { validateFilledCustomInsight } from '../../common/validators';
import { RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS } from '@silverfort-nodelibs/common/utils/insight';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';

const {
    ID_FIELD,
    NAME,
    LAST_MODIFIED,
    INCLUDE_NAMING_CONVENTIONS,
    EXCLUDE_NAMING_CONVENTIONS,
    USERS_AND_GROUPS,
    EXCLUDED_USERS_AND_GROUPS,
    IS_STATIC_INSIGHT,
    INSIGHT_ENTITY_TYPE
} = CUSTOM_INSIGHTS_FIELDS;
const { CONDITION, CONDITION_TYPE } = NAMING_CONVENTIONS_FIELDS;

export const fieldsReducer = (state, action) => {
    switch (action.type) {
        case actions.UPDATE_CUSTOM_INSIGHT:
            const newState = updateTabField(state, action.payload.tabKey, action.payload.fieldId, action.payload.value);
            if (action.payload.fieldId === NAME.ID) {
                newState[action.payload.tabKey].name = action.payload.value;
            }
            if (newState[action.payload.tabKey].validators.filledCustomInsight) {
                validateFilledCustomInsight(
                    newState[action.payload.tabKey],
                    INCLUDE_NAMING_CONVENTIONS.ID,
                    EXCLUDE_NAMING_CONVENTIONS.ID,
                    USERS_AND_GROUPS.ID,
                    EXCLUDED_USERS_AND_GROUPS.ID
                );
            }
            return newState;
        case actions.UPDATE_CUSTOM_INSIGHT_ARRAY:
            const updatedState = updateTabFieldArray(
                state,
                action.payload.tabKey,
                action.payload.fieldId,
                action.payload.index,
                action.payload.arrFieldId,
                action.payload.value
            );
            if (updatedState[action.payload.tabKey].validators.filledCustomInsight) {
                validateFilledCustomInsight(
                    updatedState[action.payload.tabKey],
                    INCLUDE_NAMING_CONVENTIONS.ID,
                    EXCLUDE_NAMING_CONVENTIONS.ID,
                    USERS_AND_GROUPS.ID,
                    EXCLUDED_USERS_AND_GROUPS.ID
                );
            }
            return updatedState;
        case actions.REMOVE_CUSTOM_INSIGHT_ARRAY_ITEM:
            const removedState = removeFromArray(
                state,
                action.payload.tabKey,
                action.payload.fieldId,
                action.payload.index
            );
            if (removedState[action.payload.tabKey].validators.filledCustomInsight) {
                validateFilledCustomInsight(
                    removedState[action.payload.tabKey],
                    INCLUDE_NAMING_CONVENTIONS.ID,
                    EXCLUDE_NAMING_CONVENTIONS.ID,
                    USERS_AND_GROUPS.ID,
                    EXCLUDED_USERS_AND_GROUPS.ID
                );
            }
            return removedState;
        case actions.ADD_NAMING_CONVENTION:
            const newNamingConvention = mapNamingConventions(
                [
                    {
                        conditionString: '',
                        conditionType: 1
                    }
                ],
                true
            );
            const addedState = addFieldsToArray(
                state,
                action.payload.tabKey,
                action.payload.fieldId,
                newNamingConvention[0]
            );
            if (addedState[action.payload.tabKey].validators.filledCustomInsight) {
                validateFilledCustomInsight(
                    addedState[action.payload.tabKey],
                    INCLUDE_NAMING_CONVENTIONS.ID,
                    EXCLUDE_NAMING_CONVENTIONS.ID,
                    USERS_AND_GROUPS.ID,
                    EXCLUDED_USERS_AND_GROUPS.ID
                );
            }
            return addedState;
        case actions.DISCARD_NEW_CUSTOM_INSIGHT:
        case actions.ADD_CUSTOM_INSIGHT:
            const insightId = action.payload.customInsightId || `${uuidv4()}_custom_rule`;
            const name = action.payload.insightName || 'New Custom Exposure';
            const entityType = action.payload.entityType;
            return {
                ...state,
                [insightId]: {
                    id: insightId,
                    name,
                    fields: mapCustomInsight({ id: insightId, entityType }, true),
                    validators: {
                        filledCustomInsight: [
                            INCLUDE_NAMING_CONVENTIONS.ID,
                            EXCLUDE_NAMING_CONVENTIONS.ID,
                            USERS_AND_GROUPS.ID,
                            EXCLUDED_USERS_AND_GROUPS.ID
                        ],
                        uniqueName: { fieldId: NAME.ID, caseSensitive: false }
                    }
                }
            };
        case actions.DELETE_CUSTOM_INSIGHT:
            const deletedState = { ...state };
            delete deletedState[action.payload.customInsightId];
            return deletedState;
        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

export const initFieldsState = ({ settings }) => {
    const state = {};

    for (const customInsight of settings.StaticCustomInsights) {
        setCustomInsightOnState(customInsight, settings.StaticCustomInsights);
    }

    return state;

    function setCustomInsightOnState(customInsight, customInsightsArray) {
        const name = RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS[customInsight.name]?.NAME;
        state[customInsight.id] = {
            id: customInsight.id,
            name: name,
            fields: mapToCustomInsightsFields(customInsightsArray, customInsight.id),
            validators: {
                filledCustomInsight: [
                    INCLUDE_NAMING_CONVENTIONS.ID,
                    EXCLUDE_NAMING_CONVENTIONS.ID,
                    USERS_AND_GROUPS.ID,
                    EXCLUDED_USERS_AND_GROUPS.ID
                ],
                uniqueName: undefined
            }
        };
    }
};

export const mapToSettings = (saveAll, selectedTab, fields, currentSettings) => {
    const newSettings = {};
    if (saveAll) {
        newSettings.StaticCustomInsights = [];
        for (const tabKey of Object.keys(fields)) {
            const tabFields = fields[tabKey].fields;
            const isDirty = isDirtyTab(fields[tabKey]);
            const CustomInsights = newSettings.StaticCustomInsights;
            CustomInsights.push(mapFieldsToSettings(tabFields, isDirty));
        }
    } else {
        newSettings.StaticCustomInsights = [...currentSettings.StaticCustomInsights];

        handleDeletedInsights(newSettings.StaticCustomInsights);

        const tabFields = fields[selectedTab].fields;
        const isDirty = isDirtyTab(fields[selectedTab]);
        setTabFieldsOnInsights(tabFields, isDirty);
    }
    return newSettings;

    function setTabFieldsOnInsights(tabFields, isDirty) {
        const CustomInsights = newSettings.StaticCustomInsights;
        const index = CustomInsights.findIndex((ci) => ci.id === selectedTab);
        if (index >= 0) {
            CustomInsights[index] = mapFieldsToSettings(tabFields, isDirty);
        } else {
            CustomInsights.push(mapFieldsToSettings(tabFields, isDirty));
        }
    }

    function handleDeletedInsights(CustomInsights) {
        const deletedIndices = [];
        for (const [index, customInsight] of CustomInsights.entries()) {
            const isExists = Object.keys(fields).find((tabKey) => tabKey === customInsight.id);
            if (!isExists) {
                deletedIndices.push(index);
            }
        }
        for (let i = deletedIndices.length - 1; i >= 0; i--) {
            CustomInsights.splice(i, 1);
        }
    }
};

function mapToCustomInsightsFields(customInsights, customInsightId) {
    for (const customInsight of customInsights) {
        if (customInsightId === customInsight.id) {
            return mapCustomInsight(customInsight);
        }
    }
}

function mapCustomInsight(customInsight, isNew) {
    let entityType = customInsight.entityType;
    let name = customInsight.name;
    let hasConditions = !!customInsight.usersAndGroups && !!customInsight.conditions;

    name = customInsight.id;

    if (!entityType) {
        entityType = RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS[customInsight.id].ENTITY_TYPE;
    }

    if (isNew && RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS[customInsight.id].ENABLE_INCLUSION) {
        hasConditions = true;
    }

    const nameFieldValidators = { required: true };

    let mappedCustomInsight = {
        [ID_FIELD.ID]: buildField(ID_FIELD, customInsight.id),
        [NAME.ID]: buildField(NAME, name || '', nameFieldValidators),
        [LAST_MODIFIED.ID]: buildField(LAST_MODIFIED, customInsight.lastModified || Date.now()),
        [EXCLUDE_NAMING_CONVENTIONS.ID]: buildField(
            EXCLUDE_NAMING_CONVENTIONS,
            mapNamingConventions(customInsight.excludedConditions)
        ),
        [EXCLUDED_USERS_AND_GROUPS.ID]: buildField(
            EXCLUDED_USERS_AND_GROUPS,
            customInsight.excludedUsersAndGroups || []
        ),
        [IS_NEW_FIELD.ID]: buildField(IS_NEW_FIELD, isNew),
        [IS_STATIC_INSIGHT.ID]: buildField(IS_STATIC_INSIGHT),
        [INSIGHT_ENTITY_TYPE.ID]: buildField(INSIGHT_ENTITY_TYPE, entityType || AD_ENTITY_TYPES.USER)
    };

    if (hasConditions) {
        mappedCustomInsight = {
            ...mappedCustomInsight,
            [INCLUDE_NAMING_CONVENTIONS.ID]: buildField(
                INCLUDE_NAMING_CONVENTIONS,
                mapNamingConventions(customInsight.conditions)
            ),
            [USERS_AND_GROUPS.ID]: buildField(USERS_AND_GROUPS, customInsight.usersAndGroups || [])
        };
    }

    return mappedCustomInsight;
}

function mapNamingConventions(conditions, isNew) {
    const namingConventions = [];
    if (conditions) {
        for (const condition of conditions) {
            namingConventions.push({
                [CONDITION.ID]: buildField(CONDITION, condition.conditionString, {
                    required: true,
                    minLength: 1,
                    noSemicolon: true
                }),
                [CONDITION_TYPE.ID]: buildField(CONDITION_TYPE, condition.conditionType),
                [IS_NEW_FIELD.ID]: buildField(IS_NEW_FIELD, isNew)
            });
        }
    }
    return namingConventions;
}

function mapFieldsToSettings(tabFields, isDirty) {
    const id = tabFields[ID_FIELD.ID].value;
    const name = tabFields[NAME.ID].value;
    const entityType = tabFields[INSIGHT_ENTITY_TYPE.ID].value;

    let mappedFields = {
        excludedConditions: tabFields[EXCLUDE_NAMING_CONVENTIONS.ID].value.map((nc) => ({
            conditionString: nc[CONDITION.ID].value,
            conditionType: nc[CONDITION_TYPE.ID].value
        })),
        id,
        lastModified: isDirty ? Date.now() : tabFields[LAST_MODIFIED.ID].value,
        name,
        excludedUsersAndGroups: tabFields[EXCLUDED_USERS_AND_GROUPS.ID].value,
        entityType
    };

    const hasConditions = !!tabFields[INCLUDE_NAMING_CONVENTIONS.ID]?.value && !!tabFields[USERS_AND_GROUPS.ID]?.value;
    if (hasConditions) {
        mappedFields = {
            ...mappedFields,
            conditions: tabFields[INCLUDE_NAMING_CONVENTIONS.ID].value.map((nc) => ({
                conditionString: nc[CONDITION.ID].value,
                conditionType: nc[CONDITION_TYPE.ID].value
            })),
            usersAndGroups: tabFields[USERS_AND_GROUPS.ID].value
        };
    }

    return mappedFields;
}
